/**
 * Day/night cycle.
 *
 * Pure timekeeping and celestial geometry - no colours, no meshes, no engine.
 * The renderer asks this for sun/moon directions and a daylight factor and
 * decides how to paint them. Mob spawning, crop growth and sleep will read the
 * same numbers later.
 */

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/** A full cycle in real seconds. Minecraft uses 20 minutes; so do we. */
export const DEFAULT_DAY_LENGTH = 20 * 60;

/**
 * Tilt of the sun's arc away from a due-east/due-west path, in radians.
 * A perfectly flat overhead arc looks artificial; a slight lean reads as a
 * real sky and makes shadows more interesting once we have them.
 */
const ORBIT_TILT = 0.28;

/** Smooth Hermite interpolation between two edges. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

export type Phase = "night" | "dawn" | "day" | "dusk";

export class DayNightCycle {
  /**
   * Normalised time of day in [0, 1).
   *   0.00 midnight · 0.25 sunrise · 0.50 noon · 0.75 sunset
   */
  t: number;

  /** Real seconds for one full cycle. */
  dayLength: number;

  /** Multiplier on the passage of time. 0 pauses the sky. */
  timeScale = 1;

  constructor(startT = 0.30, dayLength = DEFAULT_DAY_LENGTH) {
    this.t = startT;
    this.dayLength = dayLength;
  }

  update(dt: number): void {
    this.t = (this.t + (dt * this.timeScale) / this.dayLength) % 1;
    if (this.t < 0) this.t += 1;
  }

  /** Angle of the sun above the horizon path, radians. 0 at sunrise. */
  get sunAngle(): number {
    return (this.t - 0.25) * Math.PI * 2;
  }

  /**
   * Unit vector pointing at the sun.
   * Rises toward +X, passes overhead, sets toward -X.
   */
  get sunDirection(): Vec3Like {
    const a = this.sunAngle;
    const cos = Math.cos(a);
    return {
      x: cos * Math.cos(ORBIT_TILT),
      y: Math.sin(a),
      z: cos * Math.sin(ORBIT_TILT),
    };
  }

  /** The moon sits opposite the sun. */
  get moonDirection(): Vec3Like {
    const s = this.sunDirection;
    return { x: -s.x, y: -s.y, z: -s.z };
  }

  /** Height of the sun, -1 (midnight) to 1 (noon). Drives everything visual. */
  get sunElevation(): number {
    return Math.sin(this.sunAngle);
  }

  /**
   * How lit the world is, 0 to 1.
   *
   * Twilight is deliberately wide - the sun crossing the horizon should take
   * a while, not flip like a switch. The floor keeps nights navigable rather
   * than pitch black; moonlight, effectively.
   */
  get daylight(): number {
    return smoothstep(-0.22, 0.18, this.sunElevation);
  }

  /** 1 during deep night, 0 once the sky is properly lit. Fades the stars. */
  get starVisibility(): number {
    return 1 - smoothstep(-0.28, 0.02, this.sunElevation);
  }

  get phase(): Phase {
    const e = this.sunElevation;
    if (e > 0.18) return "day";
    if (e < -0.22) return "night";
    // Rising in the first half of the cycle, setting in the second.
    return this.t < 0.5 ? "dawn" : "dusk";
  }

  /** In-world clock, 24h. Handy for the HUD and for scheduling later. */
  get clock(): { hours: number; minutes: number } {
    const total = this.t * 24 * 60;
    return {
      hours: Math.floor(total / 60) % 24,
      minutes: Math.floor(total % 60),
    };
  }

  formatClock(): string {
    const { hours, minutes } = this.clock;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }
}
